/**
 * Admin Center – Nutzerverwaltung.
 *
 * Eine Tabelle Person × Bereich. Der eigene Admin-Schalter ist gesperrt, damit
 * sich niemand selbst aussperrt; bei einem Admin sind die Bereichs-Schalter
 * gesperrt, weil ein Admin ohnehin alles sieht. Neue Bereiche erscheinen
 * automatisch als weitere Spalte.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal } from '../komponenten/Modal';
import type { AdminStand } from '../typen';
import './admin.css';

export function AdminCenterModal() {
  const { modalSchliessen, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const abfrage = useQuery({
    queryKey: ['admin-nutzer'],
    queryFn: () => api.hole<AdminStand>('/admin/nutzer'),
  });

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['admin-nutzer'] });
    void queryClient.invalidateQueries({ queryKey: ['ich'] });
    void queryClient.invalidateQueries({ queryKey: ['meta'] });
  }

  const recht = useMutation({
    mutationFn: ({ id, bereich, erlaubt }: { id: string; bereich: string; erlaubt: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/recht`, { bereich, erlaubt }),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  const adminRecht = useMutation({
    mutationFn: ({ id, istAdmin }: { id: string; istAdmin: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/admin`, { istAdmin }),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  const d = abfrage.data;
  const spalten = d?.bereiche ?? [];

  return (
    <Modal
      titel="Nutzerverwaltung"
      untertitel="Zugriff pro Bereich – jeder sieht in der Navigation nur, was hier freigeschaltet ist."
      breite={720}
      abstandOben={48}
      zeigeSchliessen
      onSchliessen={modalSchliessen}
      kopfZusatz={null}
      fuss={
        <button type="button" className="adm-fertig" onClick={modalSchliessen}>
          Fertig
        </button>
      }
    >
      {!d && <div className="modal-hinweis">Nutzer werden geladen …</div>}

      {d && (
        <>
          <div className="adm-tabelle">
            <div
              className="adm-zeile adm-kopf"
              style={{ gridTemplateColumns: `1.7fr repeat(${spalten.length + 1}, .8fr)` }}
            >
              <div>Person</div>
              {spalten.map((s) => (
                <div key={s.key}>{s.kurz}</div>
              ))}
              <div>Admin</div>
            </div>

            {d.nutzer.map((p) => (
              <div
                className="adm-zeile"
                key={p.id}
                style={{ gridTemplateColumns: `1.7fr repeat(${spalten.length + 1}, .8fr)` }}
              >
                <div className="adm-person">
                  <span className="eh-avatar adm-avatar">{p.initialen}</span>
                  <span className="adm-persontext">
                    <span className="adm-name">{p.name}</span>
                    <span className="adm-zugang">{p.zugang}</span>
                  </span>
                </div>

                {spalten.map((s) => {
                  const gesperrt = p.gesperrt.bereiche;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      title={
                        s.label + (gesperrt ? ' (Admin sieht immer alles)' : '')
                      }
                      className={`adm-schalter ${p.rechte[s.key] ? 'an' : ''} ${gesperrt ? 'gesperrt' : ''}`}
                      onClick={() =>
                        !gesperrt &&
                        recht.mutate({ id: p.id, bereich: s.key, erlaubt: !p.rechte[s.key] })
                      }
                    >
                      <span />
                    </button>
                  );
                })}

                <button
                  type="button"
                  title={
                    'Admin · Einstellungen und Rechte' +
                    (p.gesperrt.admin ? ' (eigener Zugang gesperrt)' : '')
                  }
                  className={`adm-schalter ${p.istAdmin ? 'an' : ''} ${p.gesperrt.admin ? 'gesperrt' : ''}`}
                  onClick={() =>
                    !p.gesperrt.admin && adminRecht.mutate({ id: p.id, istAdmin: !p.istAdmin })
                  }
                >
                  <span />
                </button>
              </div>
            ))}
          </div>

          <div className="modal-hinweis">
            Admin sieht immer alle Bereiche und darf Rechte vergeben. Der eigene Admin-Schalter ist
            gesperrt, damit sich niemand selbst aussperrt. Neue Bereiche erscheinen hier automatisch
            als weitere Spalte.
          </div>

          <div className="modal-hinweis">
            Neue Personen bekommen ihren Zugang über das Microsoft-Konto von Eichenherz. Damit die
            Anmeldung greift, muss die E-Mail-Adresse hier hinterlegt sein.
          </div>
        </>
      )}
    </Modal>
  );
}
